/**
 * Modelo de dominio: Dispositivo.
 *
 * Un unico lugar donde vive la forma de un dispositivo y las reglas que se
 * derivan de sus datos. Antes esta logica estaba repartida por los componentes
 * (DeviceSelector calculaba "online" en tres funciones distintas, cada una
 * repitiendo la conversion de milisegundos a segundos).
 *
 * Cuando exista el backend, el contrato que devuelva la API debe normalizarse
 * aqui con `normalizeDevice`. Si el backend cambia un nombre de campo, se
 * ajusta en este archivo y no en las quince pantallas que lo consumen.
 */

/** Estados posibles de un dispositivo, derivados y no almacenados. */
export const DEVICE_STATUS = {
  ONLINE: 'online',
  STALE: 'stale',       // emitio hace poco pero no lo suficiente para fiarse
  OFFLINE: 'offline',
  NEVER_SEEN: 'never_seen',
};

/**
 * Umbrales de frescura, en segundos.
 * Derivados del intervalo real de telemetria del firmware (30 s):
 * dos ciclos perdidos es sospechoso, cuatro es una desconexion.
 */
export const FRESHNESS = {
  ONLINE_MAX_AGE: 90,
  STALE_MAX_AGE: 600,
};

export const VALVE_STATE = {
  OPEN: 'open',
  CLOSED: 'closed',
  UNKNOWN: 'unknown',
};

export const VALVE_LABEL = {
  [VALVE_STATE.OPEN]: 'Abierta',
  [VALVE_STATE.CLOSED]: 'Cerrada',
  [VALVE_STATE.UNKNOWN]: 'Sin confirmar',
};

/**
 * Normaliza el estado de valvula venga como venga.
 *
 * Hay tres representaciones circulando por el sistema y conviene reducirlas a
 * una sola aqui:
 *   - el firmware publica a AWS la cadena `valveMode` ("open" / "closed"),
 *   - su API HTTP local publica un booleano en el mismo nombre de campo,
 *   - la API acepta la accion "close" (imperativo) frente al estado "closed".
 * Cualquier otra cosa es UNKNOWN: preferimos admitir que no lo sabemos antes
 * que suponer "abierta", que es la suposicion peligrosa en una valvula de agua.
 */
export function normalizeValveState(raw) {
  if (raw === true) return VALVE_STATE.OPEN;
  if (raw === false) return VALVE_STATE.CLOSED;
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'open' || v === 'opened' || v === 'abierta') return VALVE_STATE.OPEN;
  if (v === 'closed' || v === 'close' || v === 'cerrada') return VALVE_STATE.CLOSED;
  return VALVE_STATE.UNKNOWN;
}

/** Estado de valvula que reporta el dispositivo, ya normalizado. */
export function valveStateOf(device) {
  // El ACK/telemetría confiable se persiste en `reported_valve_state`. El
  // campo `valve_state` queda únicamente como compatibilidad y puede contener
  // una telemetría heredada. La precedencia no debe usar `||`: una cadena vacía
  // en el campo autoritativo significa "sin confirmar", no permiso para caer a
  // un valor legado potencialmente obsoleto.
  return normalizeValveState(
    device?.reported_valve_state
      ?? device?.reportedValveState
      ?? device?.valve_state
      ?? device?.valveState
  );
}

/**
 * Estado al que deberia quedar la valvula tras ejecutar `action`.
 *
 * Se usa para COMPROBAR la confirmacion, no para pintarla: quien llama compara
 * este objetivo con lo que acabe reportando el dispositivo.
 * Sólo se aceptan órdenes explícitas. Una acción ambigua nunca debe inferirse
 * a partir de un estado que puede ser antiguo.
 */
export function targetValveState(action) {
  if (action === 'open') return VALVE_STATE.OPEN;
  if (action === 'close') return VALVE_STATE.CLOSED;
  return VALVE_STATE.UNKNOWN;
}

/**
 * Vista completa de la valvula: el estado Y hasta que punto es creible.
 *
 * Esta es la pieza que faltaba. `valve_state` en DynamoDB no es "el estado de
 * la valvula ahora": es el ultimo estado que el dispositivo alcanzo a reportar.
 * Mientras el equipo emite cada 30 s ambas cosas coinciden; en cuanto se calla,
 * dejan de coincidir y el dato se queda congelado. Los dos dispositivos reales
 * llevan 110 y 266 dias en silencio y siguen diciendo "open".
 *
 * Por eso el estado nunca viaja solo: viaja con `live`, que dice si podemos
 * presentarlo como actual o solo como ultimo conocido.
 */
export function deriveValveView(device, nowSeconds = Date.now() / 1000) {
  const state = valveStateOf(device);
  const status = deriveStatus(device, nowSeconds);
  const live = status === DEVICE_STATUS.ONLINE;

  return {
    state,
    label: VALVE_LABEL[state],
    deviceStatus: status,
    live,
    // Un equipo que nunca emitio no tiene "ultimo estado conocido" que mostrar.
    known: state !== VALVE_STATE.UNKNOWN && status !== DEVICE_STATUS.NEVER_SEEN,
    lastReport: formatLastSeen(device, nowSeconds),
    // Un comando MQTT a un equipo desconectado se descarta: IoT Core no encola
    // para sesiones caidas. Ofrecer el boton seria prometer algo que no ocurre.
    controllable: status === DEVICE_STATUS.ONLINE || status === DEVICE_STATUS.STALE,
  };
}

/**
 * Normaliza un timestamp a segundos.
 * El sistema mezcla dos unidades: el firmware envia `millis()` y las Lambdas
 * escriben epoch en segundos. Se resuelve en un solo sitio.
 */
export function toSeconds(timestamp) {
  if (!timestamp) return 0;
  const n = Number(timestamp);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
}

/**
 * Estado real del dispositivo, derivado de `last_seen`.
 *
 * Deliberadamente NO se usa el campo `status` que hoy guarda DynamoDB: esta
 * congelado en "online" desde que se escribio y nada lo actualiza (hallazgo A-7
 * de AUDITORIA-2026-08-14.md). Derivarlo del ultimo latido es la unica fuente
 * fiable, y ademas funciona igual contra mocks que contra el backend.
 */
export function deriveStatus(device, nowSeconds = Date.now() / 1000) {
  const lastSeen = toSeconds(device?.last_seen);
  if (lastSeen === 0) return DEVICE_STATUS.NEVER_SEEN;

  const age = nowSeconds - lastSeen;
  if (age < 0) return DEVICE_STATUS.ONLINE;            // reloj adelantado
  if (age <= FRESHNESS.ONLINE_MAX_AGE) return DEVICE_STATUS.ONLINE;
  if (age <= FRESHNESS.STALE_MAX_AGE) return DEVICE_STATUS.STALE;
  return DEVICE_STATUS.OFFLINE;
}

export function isOnline(device) {
  return deriveStatus(device) === DEVICE_STATUS.ONLINE;
}

/** Etiqueta legible del estado, para no repetir cadenas por la interfaz. */
export const STATUS_LABEL = {
  [DEVICE_STATUS.ONLINE]: 'En línea',
  [DEVICE_STATUS.STALE]: 'Señal intermitente',
  [DEVICE_STATUS.OFFLINE]: 'Desconectado',
  [DEVICE_STATUS.NEVER_SEEN]: 'Nunca conectado',
};

/** Calidad de señal a partir del RSSI, o null si el dispositivo no esta en linea. */
export function signalQuality(device) {
  if (!isOnline(device)) return null;
  const rssi = Number(device?.wifi_rssi);
  if (!Number.isFinite(rssi)) return null;
  if (rssi > -60) return 'strong';
  if (rssi > -75) return 'medium';
  return 'weak';
}

/** Tiempo transcurrido en lenguaje natural. */
export function formatLastSeen(device, nowSeconds = Date.now() / 1000) {
  const lastSeen = toSeconds(device?.last_seen);
  if (lastSeen === 0) return 'Nunca conectado';

  const diff = Math.max(0, nowSeconds - lastSeen);
  if (diff < 45) return 'Ahora mismo';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`;
  return 'Hace más de una semana';
}

/**
 * Normaliza lo que devuelve la fuente de datos a la forma que consume la app.
 *
 * PUNTO DE MIGRACION: cuando el backend real sustituya a los mocks, este es el
 * unico lugar que debe conocer sus nombres de campo. `status` se calcula, nunca
 * se copia.
 */
export function normalizeDevice(raw) {
  if (!raw) return null;
  const reportedValveState = normalizeValveState(
    raw.reported_valve_state
      ?? raw.reportedValveState
      ?? raw.valve_state
      ?? raw.valveState
  );
  return {
    // Los campos normalizados van después de los crudos para que un valor
    // camelCase sin normalizar no pueda volver a sobrescribirlos.
    ...raw,
    deviceId: raw.device_id ?? raw.deviceId,
    name: raw.device_name ?? raw.name ?? raw.device_id ?? 'Dispositivo',
    location: raw.location ?? '',
    firmwareVersion: raw.firmware_version ?? raw.firmwareVersion ?? null,
    lastSeen: toSeconds(raw.last_seen ?? raw.lastSeen),
    // Se conservan ambos nombres durante la migración, pero ambos exponen la
    // misma fuente autoritativa. Así incluso una pantalla heredada que aún lea
    // `valve_state` no puede saltarse la precedencia del estado reportado.
    valve_state: reportedValveState,
    reported_valve_state: reportedValveState,
    valveState: reportedValveState,
    reportedValveState,
    wifiRssi: raw.wifi_rssi ?? raw.wifiRssi ?? null,
    flowLitersTotal: Number(raw.flow_liters ?? raw.flow_liters_total ?? 0),
    dailyConsumption: Number(raw.daily_consumption ?? raw.dailyConsumption ?? 0),
    monthlyConsumption: Number(raw.monthly_consumption ?? raw.monthlyConsumption ?? 0),
    configuration: raw.configuration ?? {},
    linkedAt: toSeconds(raw.linked_at ?? raw.linkedAt),
  };
}

/**
 * Metricas agregadas de un conjunto de dispositivos: lo que ve el usuario en la
 * vista de cuenta.
 *
 * Se calcula en el cliente MIENTRAS los datos son simulados. Con backend real
 * esto debe moverse a un endpoint dedicado (`GET /account/summary`): agregar en
 * el navegador obliga a descargar la telemetria de cada dispositivo, que es
 * justo el patron que encarece la factura.
 */
export function aggregateDevices(devices = []) {
  const list = devices.filter(Boolean);
  const byStatus = list.reduce((acc, d) => {
    const s = deriveStatus(d);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const sum = (field) => list.reduce((t, d) => t + (Number(d[field]) || 0), 0);

  // La comparativa usa la ventana movil de 24 h y no el dia natural: a primera
  // hora de la madrugada el consumo del dia es de apenas un litro y todas las
  // barras quedarian a cero, aunque el dato sea correcto.
  const withFlow = list
    .map((d) => ({
      device: d,
      value: Number(d.last_24h_consumption ?? d.daily_consumption ?? d.dailyConsumption ?? 0),
    }))
    .sort((a, b) => b.value - a.value);

  return {
    total: list.length,
    online: byStatus[DEVICE_STATUS.ONLINE] || 0,
    stale: byStatus[DEVICE_STATUS.STALE] || 0,
    offline: (byStatus[DEVICE_STATUS.OFFLINE] || 0) + (byStatus[DEVICE_STATUS.NEVER_SEEN] || 0),
    valvesClosed: list.filter((d) => valveStateOf(d) === VALVE_STATE.CLOSED).length,
    dailyConsumption: sum('daily_consumption'),
    last24hConsumption: sum('last_24h_consumption'),
    monthlyConsumption: sum('monthly_consumption'),
    alerts7d: sum('alerts_7d'),
    totalLiters: sum('flow_liters'),
    topConsumer: withFlow[0]?.device ?? null,
    lowestConsumer: withFlow.length > 1 ? withFlow[withFlow.length - 1].device : null,
    ranking: withFlow,
  };
}
